import { CustomerCommandPalette } from '@components/Common/CustomerCommandPalette';
import { CustomerEnvBanner } from '@components/Common/CustomerEnvBanner';
import { DevSettingsSelector } from '@components/Common/DevSettingsSelector';
import { MaintenanceScreen } from '@components/Common/MaintenanceScreen';
import { TemporaryProblemsScreen } from '@components/Common/TemporaryProblemsScreen';
import { ConfirmPersonalInfoDialog } from '@components/ConfirmPersonalInfo/ConfirmPersonalInfoDialog';
import { Error404PageWithTranslations } from '@components/Error/Error404PageWithTranslations';
import { BasicRouterLayout } from '@components/Layouts/BasicRouterLayout';
import { PrivateRoute } from '@components/Layouts/PrivateRoute';
import { RouterLayout } from '@components/Layouts/RouterLayout';
import { ScreenLoader } from '@components/Layouts/ScreenLoader';
import { SidebarLayout } from '@components/Layouts/SidebarLayout';
import { UserGroupSelectionDialog } from '@components/Login/UserGroupSelectionDialog';
import { i18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiToaster, NoddiTooltipProvider, useScrollToTop } from 'noddi-ui-web';
import { customerRoutes, LanguageCodeType } from 'noddi-util';
import {
  APIBanner,
  AuthProvider,
  BookingStoreProvider,
  isDevelopment,
  isLocal,
  LanguageProvider,
  NoddiErrorBoundary,
  SentryRoutes,
  TrackScreen,
  useAuthContext,
  useLanguageContext
} from 'noddi-web';
import { parse, stringify } from 'query-string';
import { useEffect, useState } from 'react';
import { Navigate, Route, useLocation, useSearchParams } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { initLocales } from '../../../packages/config';
import { VITE_ENGLISH_LOCALE } from './constants/baseUrl';
import { BSlug } from './pages/BookingBySlug';
import { ChangeBooking } from './pages/BookingBySlug/ChangeBooking';
import { FeedbackForBooking } from './pages/BookingBySlug/FeedbackForBooking';
import { FeedbackSuccess } from './pages/BookingBySlug/FeedbackSuccess';
import { PayForBooking } from './pages/BookingBySlug/PayForBooking';
import { UnableToCompleteDetails } from './pages/BookingBySlug/UnableToCompleteDetails';
import { BookingProposalRouter } from './pages/BookingProposalRouter';
import { BookingRouter } from './pages/BookingRouter';
import { BookingUpdateOffer } from './pages/BookingUpdateOffer';
import { BrandInsightsPage } from './pages/BrandInsights';
import { CampaignFromCode } from './pages/Campaigns/FromCode';
import { CampaignFromCodeLegacyRedirect } from './pages/Campaigns/FromCode/CampaignFromCodeLegacyRedirect';
import { CompanySignup } from './pages/CompanySignup';
import { CompanySignupSuccess } from './pages/CompanySignup/CompanySignupSuccess';
import { ConfirmationPage } from './pages/Confirmation';
import { DeliveryMap } from './pages/DeliveryMap';
import { EmailLogin } from './pages/EmailLogin';
import { FacilityTransferPage } from './pages/FacilityTransferPage';
import { OrderLinkRedirect } from './pages/FacilityTransferPage/OrderLinkRedirect';
import { FleetCalendarPage } from './pages/FleetCalendar/FleetCalendarPage';
import { PublicFleetCalendarPage } from './pages/FleetCalendar/PublicFleetCalendarPage';
import { Home } from './pages/Home';
import { CancelledBooking } from './pages/Home/Bookings/[id]/CancelledBooking';
import { CancelledBookingFeedBack } from './pages/Home/Bookings/[id]/CancelledBookingFeedBack';
import { EditAddress } from './pages/Home/Bookings/[id]/EditAddress';
import { EditBookingTimeWindow } from './pages/Home/Bookings/[id]/EditBookingTimeWindow';
import { HomeRouter } from './pages/HomeRouter';
import { Login } from './pages/Login';
import { Membership } from './pages/Memberships/[slug]';
import { PaymentInfo } from './pages/PaymentInfo';
import { PointOfSalePayment } from './pages/PointOfSalePayment';
import { Referrals } from './pages/PublicReferrals';
import { ResetPassword } from './pages/ResetPassword';
import { StationaryBookingRouter } from './pages/StationaryBookingRouter';
import { TireHotelRehomePublic } from './pages/TireHotelRehomePublic';
import { RequestOffer } from './pages/TireOffer/RequestOffer';
import { TireOfferRouter } from './pages/TireOfferRouter';
import { TireOfferStatus } from './pages/TireOfferStatus';
import { UserGroupSelection } from './pages/UserGroupSelection/UserGroupSelection';
import { BookingProposalWrapper } from './stores/BookingProposalWrapper';
import { useCurrentBrand } from './stores/BrandStore/brandStore';
import { NoddiBrandProvider } from './stores/BrandStore/NoddiBrandProvider';
import { NoddiSupportWidgetBridge } from './supportWidget/NoddiSupportWidgetBridge';
import { onBrandChange } from './utils/brand';
import { canonicalizeSmsLinkPathname } from './utils/canonicalizeSmsLinkPathname';
import { dynamicActivateLocale } from './utils/lingui';
import { initRemoteConfig } from './utils/remoteConfig';
import { getNoddiRemoteConfig } from './utils/remoteConfigSetup';

const isLocalOrDev = isLocal || isDevelopment;

export function App() {
  const [isLanguageLoaded, setIsLanguageLoaded] = useState(false);
  const locale = VITE_ENGLISH_LOCALE === 'true' ? 'en' : 'nb';

  useEffect(() => {
    // oxlint-disable-next-line typescript/no-floating-promises -- known gap: locale activation is fire-and-forget on mount, so a failed catalog import has no handler
    initLocales({
      setIsLanguageLoaded,
      defaultLanguage: locale,
      dynamicActivateLocale,
      supportedLocales: ['en', 'nb', 'se']
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- locale omitted: derived from a build-time env var, so init runs once on mount
  }, []);

  if (!isLanguageLoaded) {
    return null;
  }

  const NoddiServerContext = noddiAsync.NoddiServerContext;

  return (
    <NoddiErrorBoundary>
      <NoddiBrandProvider>
        <I18nProvider i18n={i18n}>
          <QueryParamProvider
            adapter={ReactRouter6Adapter}
            options={{
              searchStringToObject: parse,
              objectToSearchString: stringify
            }}
          >
            <NoddiServerContext>
              <AuthProvider>
                <CustomerEnvBanner />
                <BookingStoreProvider storeName='booking-customer'>
                  <LanguageProvider defaultLocale={locale} dynamicActivateLocale={dynamicActivateLocale}>
                    <NoddiToaster />
                    <NoddiTooltipProvider>
                      <RouterWithMaintenanceFallback />
                    </NoddiTooltipProvider>
                    <APIBanner />
                    <NoddiSupportWidgetBridge />
                    <UserGroupSelectionDialog />
                    <ConfirmPersonalInfoDialog />
                    <CustomerCommandPalette />
                    {isLocalOrDev && <DevSettingsSelector />}
                    <TrackScreen mixpanelPages />
                  </LanguageProvider>
                </BookingStoreProvider>
              </AuthProvider>
            </NoddiServerContext>
          </QueryParamProvider>
        </I18nProvider>
      </NoddiBrandProvider>
    </NoddiErrorBoundary>
  );
}

const RouterWithMaintenanceFallback = () => {
  const { currentBrand } = useCurrentBrand();

  const { isPending: isRemoteConfigLoading } = noddiAsync.getReactQuery().useQuery({
    queryKey: ['firebase'],
    queryFn: () => initRemoteConfig()
  });
  const maintenanceScreen = getNoddiRemoteConfig('maintenanceScreen');

  const { data: brandData, error: _brandDataError } = noddiAsync.useGet({
    type: URLKeys.getByDomainBrand,
    input: { brandDomain: currentBrand }
  });

  // Show maintenance screen if remote config loaded and indicates it should be shown, do not wanna show loading screen if loading remote config, as 99.9 % of time we dont wanna show maintenance screen and it would just add unnecessary loading time on app start
  if (!isRemoteConfigLoading && maintenanceScreen?.shouldShow) {
    return <MaintenanceScreen maintenanceScreen={maintenanceScreen} />;
  }

  if (brandData?.availability === 'temporary_problems') {
    return <TemporaryProblemsScreen />;
  }

  return <Router />;
};

const Router = () => {
  const { isTokenLoadingOnMount } = useAuthContext();
  const { setCurrentLanguage } = useLanguageContext();
  const { currentBrand } = useCurrentBrand();

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const languageFromUrl = searchParams.get('lang');
  const canonicalPathname = canonicalizeSmsLinkPathname(location.pathname);

  useEffect(() => {
    if (!languageFromUrl || canonicalPathname !== location.pathname) {
      return;
    }

    setCurrentLanguage(languageFromUrl as LanguageCodeType);
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('lang');
    setSearchParams(nextSearchParams, { replace: true });
  }, [canonicalPathname, languageFromUrl, location.pathname, searchParams, setCurrentLanguage, setSearchParams]);

  useEffect(() => {
    onBrandChange(currentBrand);
  }, [currentBrand]);

  useScrollToTop();

  if (isTokenLoadingOnMount) {
    return <ScreenLoader />;
  }

  if (canonicalPathname !== location.pathname) {
    return <Navigate replace to={{ pathname: canonicalPathname, search: location.search, hash: location.hash }} />;
  }

  return (
    <SentryRoutes>
      <Route path='/' element={<RouterLayout showChat />}>
        <Route path={customerRoutes.campaignFromCode.getBasePath()} element={<CampaignFromCode />} />
        <Route
          path={customerRoutes.campaignFromCodeLegacy.getBasePath()}
          element={<CampaignFromCodeLegacyRedirect />}
        />
        <Route path={customerRoutes.referralsInfo.getBasePath()} element={<Referrals />} />
        <Route path={customerRoutes.membership.getBasePath()} element={<Membership />} />
        <Route path={customerRoutes.tireOffer.getBasePath()} element={<TireOfferRouter />} />
        <Route path={customerRoutes.tireOfferStatus.getBasePath()} element={<TireOfferStatus />} />
        <Route path={customerRoutes.login.getBasePath()} element={<Login />} />
        <Route path={customerRoutes.emailLogin.getBasePath()} element={<EmailLogin />} />
        <Route path={customerRoutes.resetPassword.getBasePath()} element={<ResetPassword />} />
        <Route path={customerRoutes.deliveryMap.getBasePath()} element={<DeliveryMap />} />
        <Route path={customerRoutes.fleetCalendarPublic.getBasePath()} element={<PublicFleetCalendarPage />} />
        <Route path='' element={<Navigate replace to={customerRoutes.userSelection.getBasePath()} />} />
        <Route path={customerRoutes.requestTireOffer.getBasePath()} element={<RequestOffer />} />
      </Route>

      <Route path='/' element={<BookingProposalWrapper />}>
        <Route path={customerRoutes.bookingProposal.getBasePath()} element={<BookingProposalRouter />} />
      </Route>

      <Route path='/' element={<RouterLayout />}>
        <Route path={customerRoutes.newBooking.getBasePath()} element={<BookingRouter />} />
        <Route path={customerRoutes.cancelledBooking.getBasePath()} element={<CancelledBooking />} />
        <Route path={customerRoutes.cancelledBookingFeedback.getBasePath()} element={<CancelledBookingFeedBack />} />

        <Route path={customerRoutes.unableToCompleteDetails.getBasePath()} element={<UnableToCompleteDetails />} />
        <Route path={customerRoutes.bookingUpdateOffer.getBasePath()} element={<BookingUpdateOffer />} />
        <Route path={customerRoutes.bookingInfo.getBasePath()} element={<BSlug />} />
        <Route path={customerRoutes.feedbackSuccess.getBasePath()} element={<FeedbackSuccess />} />

        <Route path={customerRoutes.changeBooking.getBasePath()} element={<ChangeBooking />} />
        <Route path={customerRoutes.paymentInfo.getBasePath()} element={<PaymentInfo />} />
        <Route path={customerRoutes.payForBooking.getBasePath()} element={<PayForBooking />} />
        <Route path={customerRoutes.pointOfSalePayment.getBasePath()} element={<PointOfSalePayment />} />
        <Route path={customerRoutes.feedbackForBooking.getBasePath()} element={<FeedbackForBooking />} />

        <Route path={customerRoutes.editMyBookingTimeWindow.getBasePath()} element={<EditBookingTimeWindow />} />
        <Route path={customerRoutes.editBookingAddress.getBasePath()} element={<EditAddress />} />
        <Route path={customerRoutes.storageTransferPublic.getBasePath()} element={<OrderLinkRedirect />} />
        <Route path={customerRoutes.storageFacilityTransfers.getBasePath()} element={<FacilityTransferPage />} />
        <Route path={customerRoutes.tireHotelRehomePublic.getBasePath()} element={<TireHotelRehomePublic />} />
      </Route>
      <Route path='/' element={<BasicRouterLayout />}>
        <Route path={customerRoutes.stationaryBooking.getBasePath()} element={<StationaryBookingRouter />} />
        <Route path={customerRoutes.brandInsights.getBasePath()} element={<BrandInsightsPage />} />
      </Route>

      <Route path='/' element={<PrivateRoute />}>
        <Route path={customerRoutes.userSelection.getBasePath()} element={<UserGroupSelection />} />

        <Route path='/' element={<SidebarLayout />}>
          <Route path={customerRoutes.confirmation.getBasePath()} element={<ConfirmationPage />} />
          <Route path={customerRoutes.homepage.getBasePath()} element={<Home />} />
          <Route path={customerRoutes.fleetCalendar.getBasePath()} element={<FleetCalendarPage />} />
          <Route path={customerRoutes.home.getBasePath()} element={<HomeRouter />} />
        </Route>
      </Route>

      <Route path={customerRoutes.companySignup.getBasePath()} element={<CompanySignup />} />

      <Route path={customerRoutes.companySignupSuccess.getBasePath()} element={<CompanySignupSuccess />} />
      <Route path='*' element={<Error404PageWithTranslations layout='customer' />} />
    </SentryRoutes>
  );
};
